import { matchPath } from 'react-router-dom';

/**
 * Single source of truth for the app's URLs. LayoutMain derives the visible view from the
 * current path (instead of local state), so views are shareable, bookmarkable and survive reloads.
 *
 * The selected analytic profile is deliberately NOT part of the URL (per-user preference in
 * localStorage); a shared link opens with the recipient's own profile.
 */

export type AppView =
  | 'home'
  | 'admin'
  | 'news'
  | 'research'
  | 'library'
  | 'dashboard'
  | 'distribution'
  | 'idea_sparker'
  | 'social_studio'
  | 'social_listening'
  | 'fast_tracking';

/** Base path of each view. */
export const VIEW_PATHS: Record<AppView, string> = {
  home: '/',
  // Not /admin: the edge nginx 403s /admin/* (and /internal/*) to block scanners.
  admin: '/settings',
  news: '/radar',
  research: '/research',
  library: '/library',
  dashboard: '/analytics',
  distribution: '/distribution',
  idea_sparker: '/idea-sparker',
  social_studio: '/social-studio',
  social_listening: '/social-listening',
  fast_tracking: '/fast-tracking',
};

/** Content Radar sub-tabs; the first one is the bare /radar path. */
export const RADAR_TABS = ['world', 'clusters', 'articles'] as const;
export type RadarTab = (typeof RADAR_TABS)[number];

export const ADMIN_SECTIONS = ['users', 'analytic-profiles', 'company', 'publish-venues', 'models', 'skills', 'tools', 'applications'] as const;
export type AdminSection = (typeof ADMIN_SECTIONS)[number];

export const DISTRIBUTION_TABS = ['ready', 'scheduled', 'published'] as const;
export type DistributionTab = (typeof DISTRIBUTION_TABS)[number];

/** Library draft-type tabs, in the order of the Library tab bar; the first is the bare /library path. */
export const LIBRARY_TABS = ['articles', 'podcasts', 'recipes', 'movies', 'events'] as const;
export type LibraryTab = (typeof LIBRARY_TABS)[number];

export type AppLocation = {
  view: AppView;
  /** Sub-tab / section segment (radar tab, admin section, distribution tab, library tab). */
  section?: string;
  /** Selected item id (notebook, podcast, campaign, radar cluster). */
  itemId?: string;
};

const safeDecode = (s: string) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** Returns null for paths that aren't an app view (→ Not Found). */
export const parseAppLocation = (pathname: string): AppLocation | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { view: 'home' };

  const m = (pattern: string) => matchPath({ path: pattern, end: true }, path);
  let match;

  if ((match = m('/radar/clusters/:itemId'))) {
    // react-router-dom 6.x matchPath expects an already-decoded pathname and only undoes %2F, while
    // location.pathname is encoded; cluster ids can be titles. Re-check on a router upgrade (v7 changes
    // decoding); the radarClusterPath round-trip test in appRoutes.test.ts will flag it.
    return { view: 'news', section: 'clusters', itemId: safeDecode(match.params.itemId ?? '') };
  }
  if ((match = m('/radar/:tab?'))) {
    const tab = match.params.tab;
    if (!tab) return { view: 'news', section: 'world' };
    return (RADAR_TABS as readonly string[]).includes(tab) ? { view: 'news', section: tab } : null;
  }
  if ((match = m('/settings/:section?'))) {
    const section = match.params.section;
    if (!section) return { view: 'admin' };
    return (ADMIN_SECTIONS as readonly string[]).includes(section) ? { view: 'admin', section } : null;
  }
  if ((match = m('/distribution/:tab?'))) {
    const tab = match.params.tab;
    if (!tab) return { view: 'distribution', section: 'ready' };
    return (DISTRIBUTION_TABS as readonly string[]).includes(tab) ? { view: 'distribution', section: tab } : null;
  }
  if ((match = m('/library/drafts/:itemId'))) {
    // Any draft kind (article / recipe / movie / event); the Library picks the tab from the draft.
    return { view: 'library', itemId: match.params.itemId };
  }
  if ((match = m('/library/podcasts/:itemId'))) {
    return { view: 'library', section: 'podcasts', itemId: match.params.itemId };
  }
  if ((match = m('/library/:tab?'))) {
    const tab = match.params.tab;
    if (!tab) return { view: 'library', section: 'articles' };
    return (LIBRARY_TABS as readonly string[]).includes(tab) ? { view: 'library', section: tab } : null;
  }
  if ((match = m('/research/:itemId?'))) {
    return { view: 'research', itemId: match.params.itemId };
  }
  if ((match = m('/social-studio/:itemId?'))) {
    return { view: 'social_studio', itemId: match.params.itemId };
  }

  const simple = (Object.entries(VIEW_PATHS) as [AppView, string][]).find(([, p]) => p === path);
  return simple ? { view: simple[0] } : null;
};

export const radarPath = (tab: RadarTab) => (tab === 'world' ? VIEW_PATHS.news : `${VIEW_PATHS.news}/${tab}`);
/** Clusters tab with one cluster open (filters stay in the query string, see radarUrlState). */
export const radarClusterPath = (clusterId: string) => `${radarPath('clusters')}/${encodeURIComponent(clusterId)}`;
export const adminPath = (section?: AdminSection) => (section ? `${VIEW_PATHS.admin}/${section}` : VIEW_PATHS.admin);
export const distributionPath = (tab: DistributionTab) =>
  tab === 'ready' ? VIEW_PATHS.distribution : `${VIEW_PATHS.distribution}/${tab}`;
export const libraryTabPath = (tab: LibraryTab) => (tab === 'articles' ? VIEW_PATHS.library : `${VIEW_PATHS.library}/${tab}`);
export const libraryDraftPath = (notebookId: string) => `${VIEW_PATHS.library}/drafts/${encodeURIComponent(notebookId)}`;
export const libraryPodcastPath = (podcastId: string) => `${VIEW_PATHS.library}/podcasts/${encodeURIComponent(podcastId)}`;
export const researchPath = (notebookId?: string) =>
  notebookId ? `${VIEW_PATHS.research}/${encodeURIComponent(notebookId)}` : VIEW_PATHS.research;
export const socialStudioPath = (campaignId?: string) =>
  campaignId ? `${VIEW_PATHS.social_studio}/${encodeURIComponent(campaignId)}` : VIEW_PATHS.social_studio;
